import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function parseISO(value) {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDisplay(value) {
  const d = parseISO(value);
  if (!d) return '';
  return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

// A calendar picker styled to match the studio-desk system — the native
// browser date input looks like generic chrome next to the serif/mono type
// system used everywhere else here.
function DatePicker({ value, onChange, disabled, placeholder }) {
  const [open, setOpen] = useState(false);
  const selected = parseISO(value);
  const [viewDate, setViewDate] = useState(selected || new Date());
  const wrapRef = useRef();

  useEffect(() => {
    if (selected) setViewDate(selected);
  }, [value]); // eslint-disable-line

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startWeekday = firstOfMonth.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isSelected = (d) => selected && selected.getFullYear() === year && selected.getMonth() === month && selected.getDate() === d;
  const today = new Date();
  const isToday = (d) => today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;

  const pick = (d) => {
    onChange(toISO(new Date(year, month, d)));
    setOpen(false);
  };

  const changeMonth = (delta) => {
    setViewDate(new Date(year, month + delta, 1));
  };

  return (
    <Wrap ref={wrapRef}>
      <Field
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        hasValue={!!value}
      >
        <span>{value ? formatDisplay(value) : (placeholder || 'Select date')}</span>
        <CalIcon>
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
            <rect x="2" y="3.5" width="12" height="10.5" rx="1.3" stroke="currentColor" strokeWidth="1.3" />
            <path d="M2 6.5h12M5 2v3M11 2v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
        </CalIcon>
      </Field>

      {open && (
        <Popover>
          <PopHeader>
            <NavBtn type="button" onClick={() => changeMonth(-1)} aria-label="Previous month">‹</NavBtn>
            <MonthLabel>{MONTHS[month]} {year}</MonthLabel>
            <NavBtn type="button" onClick={() => changeMonth(1)} aria-label="Next month">›</NavBtn>
          </PopHeader>

          <WeekRow>
            {WEEKDAYS.map((w, i) => <WeekCell key={i}>{w}</WeekCell>)}
          </WeekRow>

          <Grid>
            {cells.map((d, i) => d === null ? (
              <DayCell key={i} empty />
            ) : (
              <DayCell
                key={i}
                type="button"
                as="button"
                selected={isSelected(d)}
                today={isToday(d)}
                onClick={() => pick(d)}
              >
                {d}
              </DayCell>
            ))}
          </Grid>

          <PopFooter>
            <TodayBtn type="button" onClick={() => { const t = new Date(); setViewDate(t); pick(t.getDate()); }}>
              Today
            </TodayBtn>
          </PopFooter>
        </Popover>
      )}
    </Wrap>
  );
}

const Wrap = styled.div`
  position: relative;
  width: 100%;
`;

const Field = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: var(--bg-3);
  border: 1px solid var(--border);
  color: ${p => p.hasValue ? 'var(--text)' : 'var(--text-3)'};
  padding: 9px 12px;
  border-radius: var(--radius);
  font-size: 13px;
  font-family: var(--font-mono);
  text-align: left;
  transition: border-color 0.1s;

  &:hover:not(:disabled) { border-color: var(--border-2); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CalIcon = styled.span`
  color: var(--text-3);
  flex-shrink: 0;
  display: flex;
`;

const Popover = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 40;
  width: 280px;
  background: var(--bg-2);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  padding: 14px;
`;

const PopHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const NavBtn = styled.button`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius);
  color: var(--text-2);
  font-size: 16px;
  line-height: 1;
  &:hover { background: var(--bg-3); color: var(--text); }
`;

const MonthLabel = styled.span`
  font-family: var(--font-display);
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
`;

const WeekRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 4px;
`;

const WeekCell = styled.div`
  text-align: center;
  font-family: var(--font-mono);
  font-size: 9.5px;
  color: var(--text-3);
  padding: 4px 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
`;

const DayCell = styled.div`
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  border-radius: var(--radius);
  visibility: ${p => p.empty ? 'hidden' : 'visible'};
  background: ${p => p.selected ? 'var(--accent)' : 'transparent'};
  color: ${p => p.selected ? '#ffffff' : 'var(--text)'};
  font-weight: ${p => p.selected || p.today ? '600' : '400'};
  box-shadow: ${p => (p.today && !p.selected) ? 'inset 0 0 0 1px var(--accent-border)' : 'none'};

  ${p => !p.empty && `
    &:hover { background: ${p.selected ? 'var(--accent)' : 'var(--bg-3)'}; }
  `}
`;

const PopFooter = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--border);
  display: flex;
  justify-content: flex-end;
`;

const TodayBtn = styled.button`
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--accent-text);
  &:hover { text-decoration: underline; }
`;

export default DatePicker;
